import React, { useEffect, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import { SparkTelephonyDeploymentFormData, SparkTelephonyDeploymentProps } from './SparkTelephonyDeploymentForm';
import styled from '@emotion/styled';
import FormFieldCard from '../../../../shared/components/forms/FormFieldCard';
import RadioCardGroupWithAudio from '../../../../shared/components/forms/RadioCardGroupWithAudio';
import PanelContainer from '../../../../shared/components/structure/Panels/PanelContainer';
import ScrollablePanelBody from '../../../../shared/components/structure/ScrollablePanelBody';
import Button from '../../../../shared/components/general/Button';
import { css } from '@emotion/css';
import { breakpoint_small } from '../../../../shared/constants/breakpoints';
import PanelHeaderContainer from '../../../../shared/components/structure/Panels/PanelHeaderContainer';
import { color_shades_dark, color_shades_lighter, color_shades_darker } from '../../../../shared/constants/colors';
import Switch from '../../../../shared/components/forms/Switch';
import TelephonyDeploymentContainer from '../../../../shared/hooks/TelephonyDeploymentContainer';
import { OutboundProvisioningProgressModal } from './OutboundProvisioningProgressModal';
import FieldError from '../../../../shared/components/forms/FieldError';
import Loader from '../../../../shared/components/general/Loader';
import OutboundSetupChoice from './OutboundSetupChoice';

const instructionsIcon = require('../../../../shared/content/images/info_icon.svg');

const SparkTelephonyDeployment: React.FC<SparkTelephonyDeploymentProps & FormikProps<SparkTelephonyDeploymentFormData>> = (props) => {

    const telephonyDeploymentContainer = TelephonyDeploymentContainer.useContainer();
    const [showOutboundModal, setShowOutboundModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const saveInProgress = useRef(false);
    const mounted = useRef(true);
    const isBusy = isSaving || props.isSubmitting || props.isPhoneSetupPending || telephonyDeploymentContainer.isLoading;

    useEffect(() => () => { mounted.current = false; }, []);

    const clearTelephonyDeploymentErrors = () => {
        props.clearTelephonyDeploymentErrors();
        props.setPhoneNumberDeploymentErrorText("");
    };

    const handleTelephonySubmit = async () => {
        if (saveInProgress.current || isBusy) return;
        saveInProgress.current = true;
        setIsSaving(true);
        try {
            await props.submitForm();
        } catch {
            props.setPageErrorText('Could not finish phone setup. Please try again.');
        } finally {
            saveInProgress.current = false;
            if (mounted.current) setIsSaving(false);
        }
    };

    const handleSkipPhoneNumberSection = () => {
        if (saveInProgress.current || isBusy) return;
        clearTelephonyDeploymentErrors();
        props.handleRemoveSection(props.telephonyDeploymentSection);
        props.removeSectionFromIncompleteSections(props.telephonyDeploymentSection);
    };

    return (
        <SparkTelephonyDeloymentWrapper>
            <PanelHeaderContainer>
                <InstructionsIcon src={instructionsIcon} />
                <InstructionsText>Complete the fields below</InstructionsText>
                <div className={buttonContainerStyle}>
                    <Button
                        disabled={isBusy}
                        themes={["white-small"]}
                        text="Previous"
                        type="button"
                        onClick={props.handlePreviousSection}
                    />
                    <Button
                        disabled={isBusy}
                        themes={["primary-small"]}
                        text="Save & Next"
                        type="button"
                        onClick={handleTelephonySubmit}
                    />
                </div>
            </PanelHeaderContainer>
            <SparkTelephonyDeploymentFormWrapper>
                <PanelContainer className={panelContainerStyle}>
                    <ScrollablePanelBody>
                        {isBusy ? (
                            <PhoneConfigurationLoader role="status" aria-label="Loading phone configuration">
                                <Loader />
                            </PhoneConfigurationLoader>
                        ) : <form onSubmit={event => { event.preventDefault(); handleTelephonySubmit(); }}>
                            <FormFieldCardsWrapper>
                                <Title>Phone Number</Title>
                                <FormFieldCardWrapper>
                                    <FormFieldCard
                                        title="Add Spanish Line"
                                        required={false}
                                        tip="Enable to automatically set up a Spanish language line for this assistant using the selected voice's Spanish configuration."
                                    >
                                        <CustomSwitch
                                            name="addSpanishLine"
                                            label="Add Spanish Line"
                                            detail="Automatically creates a Spanish language telephony deployment alongside the English line."
                                            checked={props.values.addSpanishLine}
                                            onChange={(checked) => props.setFieldValue('addSpanishLine', checked)}
                                        />
                                    </FormFieldCard>
                                </FormFieldCardWrapper>
                                {props.assistantType === 'dental-scheduling' && !props.isOutboundEnabledOnAnyDeployment && (
                                    <FormFieldCardWrapper>
                                        <FormFieldCard
                                            title="Add Outbound Calling"
                                            required={false}
                                            tip="Automatically set up outbound calling and scheduling for this location."
                                        >
                                            <CustomSwitch
                                                name="addOutbound"
                                                label="Add Outbound"
                                                detail="Enable outbound calling for this location. Outbound calls won't be scheduled until you configure a schedule and go live."
                                                checked={props.values.addOutbound}
                                                onChange={(checked) => {
                                                    props.setFieldValue('addOutbound', checked);
                                                    if (!checked) {
                                                        props.setFieldValue('outboundSaved', false);
                                                        props.setFieldValue('outboundSetupMode', 'manual');
                                                        props.setFieldValue('outboundSourceApplicationId', '');
                                                    }
                                                }}
                                            />
                                            {props.values.addOutbound && (
                                                <OutboundSetupChoice
                                                    applicationId={props.applicationId}
                                                    orgId={props.orgId}
                                                    mode={props.values.outboundSetupMode}
                                                    selectedSourceId={props.values.outboundSourceApplicationId}
                                                    manualScheduleSaved={props.values.outboundSetupMode === 'manual' && props.values.outboundSaved}
                                                    onModeChange={(mode) => {
                                                        props.setFieldValue('outboundSetupMode', mode);
                                                        props.setFieldValue('outboundSaved', false);
                                                    }}
                                                    onSourceChange={(source) => {
                                                        props.setFieldValue('outboundSourceApplicationId', source?.applicationId || '');
                                                        props.setFieldValue('outboundSaved', false);
                                                        if (source) {
                                                            props.setFieldValue('outboundSchedulePolicy', source.schedulePolicy);
                                                            props.setFieldValue('outboundCallingEnabled', false);
                                                        }
                                                    }}
                                                    onOpenSchedule={() => setShowOutboundModal(true)}
                                                />
                                            )}
                                            {props.values.addOutbound && !props.values.outboundSaved && props.submitCount > 0 && (
                                                <FieldError name="outboundSaved" ignoreTouched={true} />
                                            )}
                                        </FormFieldCard>
                                    </FormFieldCardWrapper>
                                )}
                                <FormFieldCardWrapper>
                                    <FormFieldCard
                                        title="Voice"
                                        required={true}
                                        tip="Select your assistant's voice from the list below."
                                    >
                                        <RadioCardGroupWithAudioWrapper>
                                            <RadioCardGroupWithAudio
                                                onChange={(e) => props.setFieldValue("ttsVoice", e.value)}
                                                options={props.voiceOptions}
                                                value={props.values.ttsVoice}
                                            />
                                        </RadioCardGroupWithAudioWrapper>
                                    </FormFieldCard>
                                </FormFieldCardWrapper>
                            </FormFieldCardsWrapper>
                             <ButtonWrapper>
                                <StyledSaveAndNextButton
                                    disabled={isBusy}
                                    themes={["primary"]}
                                    text="Save & Next"
                                    type="button"
                                    onClick={() => handleTelephonySubmit()}
                                />
                                <StyledSkipButton
                                    disabled={isBusy}
                                    themes={["primary"]}
                                    text="Skip"
                                    type="button"
                                    onClick={() => handleSkipPhoneNumberSection()}
                                />
                            </ButtonWrapper>
                        </form>}
                        {showOutboundModal && !isBusy && (
                            <OutboundProvisioningProgressModal
                                applicationId={props.applicationId}
                                environmentId={props.environmentId || ""}
                                organizationName={props.orgName || ""}
                                locationName={props.appName || ""}
                                appName={props.appName || ""}
                                pmsName=""
                                defaultOfficeId=""
                                timeZone=""
                                medicalIntegrationConfigId=""
                                hasSpanish={props.values.addSpanishLine}
                                onClose={() => setShowOutboundModal(false)}
                                onSuccess={() => {
                                    setShowOutboundModal(false);
                                    props.setFieldValue('outboundSaved', true);
                                    props.loadTelephonyDeployments();
                                }}
                                orgId={props.orgId}
                                deferProvisioning={true}
                                initialSchedulePolicy={props.values.outboundSchedulePolicy}
                                initialOutboundCallingEnabled={props.values.outboundCallingEnabled}
                                applicationFeatureFlags={props.applicationFeatureFlags}
                                onSaveConfig={(schedulePolicy, outboundCallingEnabled) => {
                                    props.setFieldValue('outboundSchedulePolicy', schedulePolicy);
                                    props.setFieldValue('outboundCallingEnabled', outboundCallingEnabled);
                                    props.setFieldValue('outboundSaved', true);
                                    setShowOutboundModal(false);
                                }}
                            />
                        )}
                    </ScrollablePanelBody>
                </PanelContainer>
            </SparkTelephonyDeploymentFormWrapper>
        </SparkTelephonyDeloymentWrapper>
    )
}

const SparkTelephonyDeloymentWrapper = styled.div``;

const PhoneConfigurationLoader = styled.div`
    height: calc(100vh - 144px);
    position: relative;
    width: 100%;
`;

const CustomSwitch = styled(Switch)`
    margin: 24px 0;
`;

const RadioCardGroupWithAudioWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 16px;
`;

const FormFieldCardWrapper = styled.div`
    padding: 16px 0px;
    position: relative;
    .field-triangle {
        position: absolute;
        width: 24px;
        height: 24px;
        background: ${color_shades_lighter};
        right: -44px;
        top: 64px;
        transform: rotate(45deg);
        border-left: 1px solid ${color_shades_dark};
        border-bottom: 1px solid ${color_shades_dark};
    }
`;


const FormFieldCardsWrapper = styled.div`
    padding: 32px;
    h3 {
        margin-bottom: 16px;
        font-style: normal;
        font-weight: normal;
        font-size: 18px;
        line-height: 24px;
    }
    ${breakpoint_small} {
        padding: 16px;
        h3 {
            margin-bottom: 0px;
        }
    }
`;

const InstructionsIcon = styled.img`
    width: 24px;
    height: 24px;
    margin: 0 8px 0 32px;
    ${breakpoint_small} {
        display: none;
    }
`;

const InstructionsText = styled.p`
    ${breakpoint_small} {
        display: none;
    }
`;

const buttonContainerStyle = css`
    margin-right: 16px;
    margin-left: auto;
    button {
        margin: 0 16px 0 0;
    }
    ${breakpoint_small} {
        margin-right: 0;
    }
`

const SparkTelephonyDeploymentFormWrapper = styled.div`
    h3 {
        margin-bottom: 16px;
        font-style: normal;
        font-weight: normal;
        font-size: 18px;
        line-height: 24px;
    }
    ${breakpoint_small} {
        padding: 16px;
        h3 {
            margin-bottom: 0px;
        }
    }
`;

const StyledSaveAndNextButton = styled(Button)`
    margin-top: 0px;
    margin-right: 32px;
    ${breakpoint_small} {
        margin-right: 0;
        width: 100px;
        font-size: 12px;
    }
`;

const StyledSkipButton = styled(Button)`
    margin-top: 0px;
    margin-right: 32px;
    ${breakpoint_small} {
        margin-right: 16px;
        width: 100px;
        font-size: 12px;
    }
`;

const panelContainerStyle = css`
    display: flex;
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 0 0 88px 18px;
    ${breakpoint_small} {
        justify-content: flex-end;
    }
`;

const Title = styled.h3`
    font-size: 18px;
    font-weight: normal;
    margin-bottom: 16px;
`;

export default SparkTelephonyDeployment;

